import useSWR from "swr";

import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { AudienceSelection, AudienceSelectionCity } from "@/types/marketing";

const fetcher = (url: string) => fetch(url, { credentials: "include" }).then((response) => response.json());

interface AudienceSelectorProps {
  value?: AudienceSelection;
  onChange: (selection: AudienceSelection) => void;
}

const DEFAULT_SELECTION: AudienceSelection = {
  cities: [],
  interests: [],
  ageRanges: [],
  platforms: ["instagram", "tiktok"],
};

export function AudienceSelector({ value, onChange }: AudienceSelectorProps) {
  const selection = value ?? DEFAULT_SELECTION;
  const { data, isLoading } = useSWR<SuggestionPayload>("/marketing/audience/suggestions", fetcher);

  const handleCityToggle = (cityLabel: string, defaultRadius: number) => {
    const exists = selection.cities.find((city) => city.label === cityLabel);
    let updated: AudienceSelectionCity[];
    if (exists) {
      updated = selection.cities.filter((city) => city.label !== cityLabel);
    } else {
      updated = [...selection.cities, { label: cityLabel, radius: defaultRadius }];
    }
    onChange({ ...selection, cities: updated });
  };

  const updateCityRadius = (cityLabel: string, radius: number) => {
    const updated = selection.cities.map((city) =>
      city.label === cityLabel ? { ...city, radius } : city,
    );
    onChange({ ...selection, cities: updated });
  };

  const toggleInterest = (interest: string) => {
    const list = selection.interests.includes(interest)
      ? selection.interests.filter((value) => value !== interest)
      : [...selection.interests, interest];
    onChange({ ...selection, interests: list });
  };

  const toggleAgeRange = (range: string) => {
    const list = selection.ageRanges.includes(range)
      ? selection.ageRanges.filter((value) => value !== range)
      : [...selection.ageRanges, range];
    onChange({ ...selection, ageRanges: list });
  };

  const togglePlatform = (platform: string) => {
    const list = selection.platforms.includes(platform)
      ? selection.platforms.filter((value) => value !== platform)
      : [...selection.platforms, platform];
    onChange({ ...selection, platforms: list });
  };

  return (
    <Card className="space-y-3 border-border/70 bg-background/40 p-4">
      <div>
        <p className="text-sm font-semibold text-foreground">Target Audience</p>
        <p className="text-xs text-muted-foreground">Refine geo + interest targeting per platform.</p>
      </div>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading audience suggestions…</p>
      ) : data ? (
        <div className="space-y-4">
          <section className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Cities</p>
            <div className="grid gap-2 md:grid-cols-2">
              {data.top_cities.map((city) => {
                const label = `${city.city}${city.state ? `, ${city.state}` : ""}`;
                const selected = selection.cities.find((entry) => entry.label === label);
                return (
                  <div key={label} className="rounded-lg border border-border/70 bg-background/30 p-3">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={Boolean(selected)} onCheckedChange={() => handleCityToggle(label, city.radius ?? 25)} />
                      <div>
                        <p className="text-sm font-semibold text-foreground">{label}</p>
                        <p className="text-xs text-muted-foreground">{city.reason ?? "High engagement"}</p>
                      </div>
                    </div>
                    {selected ? (
                      <div className="mt-2 flex items-center gap-2">
                        <Input
                          type="number"
                          min={5}
                          value={selected.radius}
                          onChange={(event) => updateCityRadius(label, Number(event.target.value) || 5)}
                          className="w-20 bg-background/50 text-foreground"
                        />
                        <p className="text-xs text-muted-foreground">mile radius</p>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>

          <section className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Interests</p>
            <div className="flex flex-wrap gap-2">
              {data.interest_clusters.map((interest) => (
                <Badge
                  key={interest}
                  onClick={() => toggleInterest(interest)}
                  className={cn(
                    "cursor-pointer border border-border bg-background/40 text-foreground/80",
                    selection.interests.includes(interest) && "border-success/50 bg-success/10 text-success",
                  )}
                >
                  {interest}
                </Badge>
              ))}
            </div>
          </section>

          <section className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Age ranges</p>
            <div className="flex flex-wrap gap-2">
              {data.age_ranges.map((range) => (
                <Button
                  key={range}
                  variant={selection.ageRanges.includes(range) ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleAgeRange(range)}
                >
                  {range}
                </Button>
              ))}
            </div>
          </section>

          <section className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Platforms</p>
            <div className="flex flex-wrap gap-2">
              {["instagram", "tiktok", "youtube"].map((platform) => (
                <Button
                  key={platform}
                  variant={selection.platforms.includes(platform) ? "default" : "outline"}
                  size="sm"
                  onClick={() => togglePlatform(platform)}
                >
                  {platform}
                </Button>
              ))}
            </div>
          </section>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No audience suggestions available.</p>
      )}
    </Card>
  );
}

type SuggestionPayload = {
  top_cities: Array<{ city: string; state?: string; radius?: number; reason?: string }>;
  age_ranges: string[];
  interest_clusters: string[];
};

function cn(...classes: Array<string | undefined | false | null>) {
  return classes.filter(Boolean).join(" ");
}
